import { useEffect, useState } from "react";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Link2, Trash2, Eye } from "lucide-react";
import { CopyButton } from "@/components/translator/shared";

const LABELS = { account_country: "País", bank_name: "Banco", holder_name: "Titular", id_document: "Documento", iban: "IBAN", swift_bic: "SWIFT/BIC", routing_number: "Nº de ruta", holder_address: "Dirección", email: "Email", phone: "Teléfono", signed_at: "Firmado" };

export default function FormsPage() {
  const [links, setLinks] = useState([]);
  const [who, setWho] = useState("");
  const [view, setView] = useState(null);
  const url = (t) => `${window.location.origin}/formulario/${t}`;

  const load = () => api.get("/forms/bank-links").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!who.trim()) return toast.error("Escribe el nombre");
    try {
      await api.post("/forms/bank-links", { person_name: who });
      toast.success("Enlace creado");
      setWho("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const open = async (id) => { const { data } = await api.get(`/forms/bank-links/${id}/data`); setView(data); };
  const remove = async (id) => { if (!window.confirm("¿Eliminar enlace y datos?")) return; await api.delete(`/forms/bank-links/${id}`); load(); };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-5xl" data-testid="forms-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6 flex items-center gap-3"><FileText className="text-[#E63946]" size={30} /> Formularios</h1>
      <div className="border-2 border-black bg-white p-5 mb-6">
        <div className="font-display font-bold mb-3">Datos bancarios · generar enlace</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={who} onChange={(e) => setWho(e.target.value)} maxLength={120} placeholder="Nombre completo de la persona" className="flex-1 h-11 border border-black px-3 bg-white text-sm" data-testid="forms-person" />
          <button onClick={create} className="h-11 px-5 bg-black text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#E63946] inline-flex items-center gap-2" data-testid="forms-create"><Link2 size={14} /> Generar enlace</button>
        </div>
      </div>
      <div className="space-y-2" data-testid="forms-list">
        {links.map((l) => (
          <div key={l.id} className="border border-black bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`form-link-${l.id}`}>
            <div className="min-w-0">
              <div className="font-semibold">{l.person_name}</div>
              <div className={`text-xs ${l.status === "completed" ? "text-emerald-700" : "text-zinc-500"}`}>
                {l.status === "completed" ? `Completado · ${l.account_country} · ${l.iban_masked}` : "Pendiente de rellenar"}
              </div>
            </div>
            <div className="flex gap-2">
              {l.status === "completed" && (
                <a href={`${API}/forms/bank-links/${l.id}/pdf?auth=${encodeURIComponent(getToken())}`} target="_blank" rel="noreferrer"
                  className="px-3 py-2 border border-black text-[11px] font-semibold uppercase tracking-widest hover:bg-black hover:text-white" data-testid={`form-pdf-${l.id}`}>PDF</a>
              )}
              {l.status === "completed"
                ? <button onClick={() => open(l.id)} className="p-2 border border-black hover:bg-black hover:text-white" data-testid={`form-view-${l.id}`} aria-label="Ver datos"><Eye size={14} /></button>
                : <CopyButton text={url(l.token)} label="Copiar enlace" testId={`form-copy-${l.id}`} />}
              <button onClick={() => remove(l.id)} className="p-2 border border-black hover:bg-[#E63946] hover:text-white" data-testid={`form-delete-${l.id}`} aria-label="Eliminar"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="form-data-dialog">
          <DialogHeader><DialogTitle className="font-display">Datos bancarios (clasificado)</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-1 text-sm">
              {Object.entries(LABELS).map(([k, l]) => view[k] ? <div key={k}><span className="text-zinc-500">{l}:</span> <b className="break-all">{view[k]}</b></div> : null)}
              <img src={view.signature} alt="Firma" className="border border-black mt-2 w-full" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
